'use client';

import React, { useEffect, useMemo, useState } from 'react';

import { cn, getVideoEmbedUrl } from '@/lib/utils';

interface VideoItemProps {
  assetUrl: string;
  autoplay?: boolean;
  onClick?: () => void;
}

interface YouTubePlaylistPreviewProps {
  playlistUrl: string;
  onClick?: () => void;
}

export const VideoItem: React.FC<VideoItemProps> = ({ assetUrl, autoplay, onClick }) => {
  const { isEmbed, embedUrl, playlistUrl } = useMemo(
    () => getVideoEmbedUrl(assetUrl, autoplay),
    [assetUrl, autoplay]
  );

  if (playlistUrl) {
    return <YouTubePlaylistPreview playlistUrl={playlistUrl} onClick={onClick} />;
  }

  if (isEmbed) {
    return (
      <iframe
        className={cn('rounded-[10px] shadow-post-shadow w-full max-w-[600px] aspect-video lg:aspect-auto lg:h-[300px]')}
        src={embedUrl}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        onClick={onClick}
      />
    );
  }

  return (
    <video
      className={cn('rounded-[10px] shadow-post-shadow w-full max-w-[600px] h-[300px] object-contain')}
      src={embedUrl}
      controls
      autoPlay={autoplay || false}
      {...(autoplay && { muted: true })}
      onClick={onClick}
    />
  );
};

const YouTubePlaylistPreview: React.FC<YouTubePlaylistPreviewProps> = ({ playlistUrl, onClick }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);

  const listId = useMemo(() => new URL(playlistUrl).searchParams.get('list'), [playlistUrl]);

  useEffect(() => {
    if (!listId) return;

    let active = true;
    fetch(`/api/youtube-oembed?list=${encodeURIComponent(listId)}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!active || !data) return;
        setThumbnailUrl(data.thumbnailUrl ?? null);
        setTitle(data.title ?? null);
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [listId]);

  return (
    <a
      href={playlistUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="group relative block w-full max-w-[600px] aspect-video overflow-hidden rounded-[10px] bg-black shadow-post-shadow"
      aria-label={title ? `Open playlist: ${title}` : 'Open YouTube playlist'}
    >
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt={title ?? 'YouTube playlist'}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}

      <div className="absolute inset-0 bg-black/30 transition-colors group-hover:bg-black/40" />

      <span className="absolute right-3 top-3 flex items-center gap-1 rounded bg-black/70 px-2 py-1 text-xs font-medium text-white">
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
          <path d="M3 6h12v2H3V6zm0 5h12v2H3v-2zm0 5h8v2H3v-2zm14-5l5 3-5 3v-6z" />
        </svg>
        Playlist
      </span>

      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FF0000] transition-transform group-hover:scale-110">
          <svg viewBox="0 0 24 24" className="h-7 w-7 text-white" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </span>

      {title && (
        <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/80 to-transparent px-3 pb-2 pt-6 text-sm font-medium text-white">
          {title}
        </span>
      )}
    </a>
  );
};
